/*
 * Copyright (c) 2018 Helvia Technologies PC <contact@helvia.io>
 */

// Core imports
import { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import { Link, useParams } from 'react-router-dom';
import { Trans, useTranslation } from 'react-i18next';

// Component imports
import { AutoComplete, Button, Card, Carousel, Col, Divider, Empty, Form, Input, Row, Select, Space, Spin, Tooltip, Typography } from 'antd';
import { BookOutlined, CloseOutlined, MessageOutlined, PlayCircleOutlined, QuestionCircleOutlined, ReloadOutlined, WarningOutlined } from '@ant-design/icons';
import ReactMarkdown from 'react-markdown';
import ActivityLink from '../UI/ActivityLink';
import ArticlePreviewButton from './ArticlePreviewButton';
import { DecisionIcon } from '../Icons/DecisionIcon';

// Misc imports
import { DEFAULT_FALLBACK_ID, NODE_TYPES } from '../../constants/flowGraph';
import { useBotLanguage } from '../../utils/hooks/useBotLanguage';
import { getCurrentQueryState } from '../../store/utils/getCurrentQueryState';
import { useCreateAutomatedAnswerMutation, useUpdateAutomatedAnswerMutation } from '../../store/automatedAnswers/automatedAnswersApi';
import { truncateMarkdown } from '../../utils/string/truncateMarkdown';
import { useLazyProcessMessageQuery, useLazyProcessMessageWithPipelineIdQuery } from '../../store/bots/botsApi';
import { getArticleId, getKnowledgeBaseId } from '../../utils/transform/automatedAnswer';
import is from '../../utils/is';
import useTheme from '../../utils/hooks/useTheme';
import { truncate } from '../../utils/string/truncate';
import { API_NLP_SERVICE_URL } from '../../constants/env';
import replaceUrlParams from '../../utils/string/replaceUrlParams';
import { NAV_BOT } from '../../constants/navigation';
import { useFetchNlpPipelinesQuery } from '../../store/nlpPipelines/nlpPipelinesApi';

const TestAutomatedAnswersPanel = ({ setIsTestPanelVisible, setSelectedAA }) => {
  const [form] = Form.useForm();
  const { tenantId, orgId } = useParams();
  const { t } = useTranslation();
  const { currentBotLanguage } = useBotLanguage();
  const { messageNodeIconColor, jumpNodeIconColor, defaultNodeIconColor } = useTheme();
  const carouselRef = useRef();

  const [processMessageDefault, { isFetching: isProcessingMessageDefault, currentData: processedMessageDefault }] = useLazyProcessMessageQuery();
  const [processMessageWithNlp, { isFetching: isProcessingMessageWithNLP, currentData: processedMessageWithNlp }] = useLazyProcessMessageWithPipelineIdQuery();
  const { data: bot } = getCurrentQueryState('fetchBot', { orgId, tenantId });
  const { data: automatedAnswers } = getCurrentQueryState('fetchAutomatedAnswers', { orgId, tenantId, currentBotLanguage });
  const [, { isLoading: isCreatingAutomatedAnswer }] = useCreateAutomatedAnswerMutation({ fixedCacheKey: 'create-automated-answer-main-button-shared' });
  const [, { isLoading: isUpdatingAutomatedAnswer }] = useUpdateAutomatedAnswerMutation({ fixedCacheKey: 'update-automated-answer-main-button-shared' });
  const { data: nlpPipelines, isLoading: isLoadingNlpPipelines } = useFetchNlpPipelinesQuery({ orgId, tenantId });

  const [recentlyTestedPhrases, setRecentlyTestedPhrases] = useState([]);
  const [currentPipelineId, setCurrentPipelineId] = useState(null);

  const processedMessage = useMemo(() => {
    if(currentPipelineId === null) {
      return processedMessageDefault;
    } else {
      return processedMessageWithNlp;
    }
  }, [processedMessageDefault, processedMessageWithNlp, currentPipelineId]);

  const isProcessingMessage = useMemo(() => {
    return isProcessingMessageDefault || isProcessingMessageWithNLP;
  }, [isProcessingMessageDefault, isProcessingMessageWithNLP]);

  const examinedCorpusIdsThatExist = processedMessage?.examinedCorpus?.filter(examinedCorpus => automatedAnswers?.items?.find(automatedAnswer => automatedAnswer.id.includes(examinedCorpus.id)));
  const defaultFallbackActivity = useMemo(() => (automatedAnswers?.items || []).find(automatedAnswer => automatedAnswer.id === DEFAULT_FALLBACK_ID), [automatedAnswers]);

  useEffect(() => {

    if(bot && bot?.nlpMap && Object.keys(bot?.nlpMap).includes(currentBotLanguage)) {
      setCurrentPipelineId(null);
    }

    return () => {
      setCurrentPipelineId(null);
    };

  }, [currentBotLanguage, bot, nlpPipelines]);
  /**
   * Renders the bot messages that are part of the automated answer
   * @param {Object} activity The activity/AA object
   * @returns {JSX.Element} The bot messages
   *
  **/
  const renderBotMessages = (activity) => {
    return activity ? (
      <Fragment>
        {
          activity.content.nodes.map((node, index) => {
            if (node.type === NODE_TYPES.MESSAGE) {
              // If the  node includes a label (in the current lang) {{nlu.suggestedTextResponse}} or {{nlp.generatedText}}
              const generatedText = truncateMarkdown(
                (node.label[currentBotLanguage] || '')
                  .replaceAll('{{nlu.suggestedTextResponse}}', processedMessage.generatedText || '{{nlu.suggestedTextResponse}}')
                  .replaceAll('{{nlp.generatedText}}', processedMessage.generatedText || '{{nlp.generatedText}}'),
                {
                  replaceImages: true,
                  replaceBlankSpaces: true
                }
              );

              return (
                // "length - 1" for counting the intro node
                <div
                  className={classNames({ 'mb-4': index !== activity.content.nodes.length - 1 })}
                >
                  <Space className="mb-3 mt-1">
                    <MessageOutlined style={{ color: messageNodeIconColor }} />
                    <Typography.Text type="secondary" className="fs-sm">{t('Agent will display the below message')}</Typography.Text>
                  </Space>

                  <br/>

                  {
                    node.label[currentBotLanguage] ? (
                      <Tooltip title={generatedText}>
                        <Typography.Text italic className="bg-layout d-inline-block mr-3">
                          <ReactMarkdown
                            skipHtml
                            linkTarget="_blank"
                            className="mt-3 mx-2"
                          >
                            {truncate(generatedText, 150, '...')}
                          </ReactMarkdown>
                        </Typography.Text>
                      </Tooltip>
                    ) : (
                      <Typography.Text type="secondary" className="bg-layout d-inline-block mr-3 px-2 py-1">{t('(No response)')}</Typography.Text>
                    )
                  }
                </div>
              );
            }

            if (node.type === NODE_TYPES.JUMP) {
              return (
                <div>
                  <Space className="mb-3 mt-1">
                    <DecisionIcon style={{ color: jumpNodeIconColor }} />
                    <Typography.Text type="secondary" className="fs-sm">{t('Agent will redirect to the below flow')}</Typography.Text>
                  </Space>

                  <br/>

                  <Typography.Text italic className="bg-layout d-inline-block px-2 py-1 mr-3">
                    <ActivityLink entryPointId={node.jumpTo} />
                  </Typography.Text>
                </div>
              );
            }

            if (node.type === NODE_TYPES.KNOWLEDGE_BASE_ARTICLE) {
              const knowledgeBaseId = getKnowledgeBaseId(activity);
              const articleId = getArticleId(activity);

              const generatedText = truncateMarkdown(processedMessage.generatedText, {
                replaceImages: true,
                replaceBlankSpaces: true
              });

              // If the  node includes a label (in the current lang) {{nlu.suggestedTextResponse}} or {{nlp.generatedText}}
              const shouldDisplayGeneratedText = node.useGeneratedResponse
                || node.label[currentBotLanguage]?.includes('{{nlu.suggestedTextResponse}}')
                || node.label[currentBotLanguage]?.includes('{{nlp.generatedText}}');

              return (
                <div
                  className={classNames({ 'mb-4': index !== activity.content.nodes.length - 1 })}
                >
                  <Space className="mb-3 mt-1">
                    <BookOutlined style={{ color: defaultNodeIconColor }} />
                    <Typography.Text type="secondary" className="fs-sm">{t('Agent will display the below content')}</Typography.Text>
                  </Space>

                  <br/>

                  {
                    shouldDisplayGeneratedText && processedMessage.generatedText ? (
                      <Tooltip title={generatedText}>
                        <Typography.Text italic className="bg-layout d-inline-block mr-3">
                          <ReactMarkdown
                            skipHtml
                            linkTarget="_blank"
                            className="mt-3 mx-2"
                          >
                            {truncate(generatedText, 150, '...')}
                          </ReactMarkdown>
                        </Typography.Text>
                      </Tooltip>
                    ) : (
                      <ArticlePreviewButton
                        articleId={articleId}
                        knowledgeBaseId={knowledgeBaseId}
                        buttonText={t('Article Body')}
                        type="link"
                        className="fw-normal bg-layout h-auto p-2"
                      />
                    )
                  }
                </div>
              );
            }

            return null;
          })
        }
      </Fragment>
    ) : (
      <Typography.Text type="secondary" className="bg-layout d-inline-block px-2 py-1 mr-3">{t('(No response)')}</Typography.Text>
    );
  };

  const renderCarouselItems = () => {
    let groupedCarouselItems = [];

    for (let i = 0; i < examinedCorpusIdsThatExist.length; i = i + 2) {
      const listOfAutomatedAnswers = examinedCorpusIdsThatExist.slice(i, i + 2).map(examinedCorpus => {
        const aa = (automatedAnswers?.items || []).find(automatedAnswer => automatedAnswer.id.includes(examinedCorpus.id));
        return aa ? (
          <Fragment>
            <Space direction="vertical" className="d-block">
              <Typography.Text type="secondary" className="fs-sm d-block mb-2">
                <Space>
                  {t('Name')}
                  <Tooltip title={t('The ID of the automated answer, which is automatically generated by the console.')}>
                    <QuestionCircleOutlined />
                  </Tooltip>
                </Space>
              </Typography.Text>

              <Typography.Text italic className="bg-layout d-inline-block px-2 py-1 mr-3">
                <ActivityLink
                  entryPointId={(aa.content?.nodes || []).find(n => n.type === NODE_TYPES.INTRO)?.id}
                  setSelectedAA={setSelectedAA}
                />
              </Typography.Text>
            </Space>

            <Space direction="vertical" className="mt-3">
              <Typography.Text type="secondary" className="fs-sm d-block mb-2">
                <Space>
                  {t('Group')}
                  <Tooltip title={t('Overarching themes for grouping the automated answers that fall under a same group.')}>
                    <QuestionCircleOutlined />
                  </Tooltip>
                </Space>
              </Typography.Text>

              <Typography.Text italic className="bg-layout d-inline-block px-2 py-1 mr-3">
                {aa.group[currentBotLanguage]}
              </Typography.Text>
            </Space>

            <Space direction="vertical" className="mt-3">
              <Typography.Text type="secondary" className="fs-sm d-block mb-2">
                <Space>
                  {t('Confidence score')}
                  <Tooltip title={t('Confidence score indicates how closely this content matches the query based on semantic analysis. Scores range from 0 (low relevance) to 1 (high relevance).')}>
                    <QuestionCircleOutlined />
                  </Tooltip>
                </Space>
              </Typography.Text>

              <Typography.Text italic className="bg-layout d-inline-block px-2 py-1 mr-3">
                {examinedCorpus.confidence ? parseFloat(examinedCorpus.confidence).toFixed(2) : 0.00}
              </Typography.Text>
            </Space>
          </Fragment>
        ) : null;
      });

      groupedCarouselItems.push(
        <div className="d-block mb-4">
          <Row>
            <Col span={listOfAutomatedAnswers.length === 1 ? 24 : 12}>
              {listOfAutomatedAnswers[0]}
            </Col>

            <Col span={listOfAutomatedAnswers.length === 1 ? 0 : 12}>
              {listOfAutomatedAnswers[1]}
            </Col>
          </Row>
        </div>
      );
    }

    return groupedCarouselItems;
  };

  const renderResults = () => {
    // Initial state, no form interaction yet
    if (form.getFieldValue('trainingPhrase') === undefined) {
      return null;
    }

    // Fetching state, show loader
    if (isProcessingMessage) {
      return (
        <Row className="my-8 pt-2" justify="center">
          <Col>
            <Spin />
          </Col>
        </Row>
      );
    }

    if (processedMessage && !is.emptyObject(processedMessage)) {
      const matchingAutomatedAnswer = (automatedAnswers?.items || []).find(automatedAnswer => automatedAnswer.id === processedMessage?.matchedCorpus?.id);

      return (
        <Row className="mt-5">
          <Col span={24} className="border-left-highlight pl-3 mb-4">
            <Form.Item
              label={t('User input')}
              tooltip={{
                icon: <QuestionCircleOutlined />,
                title: t('The question you want to ask the agent to test how it responds.')
              }}
              className="mb-0"
            >
              <Typography.Text italic className="bg-layout d-inline-block px-2 py-1 mr-3">
                {`"${recentlyTestedPhrases[0]}"`}
              </Typography.Text>
            </Form.Item>
          </Col>

          {!matchingAutomatedAnswer && defaultFallbackActivity ? (
            <Col span={24} className="border-left-warning pl-3 mb-4">
              <Space className="mb-3 mt-1">
                <WarningOutlined className="text-warning" />
                <Typography.Text type="secondary" strong>{t('Unmatched User Input:')}</Typography.Text>
              </Space>
              <Col>
                <Typography.Text type="secondary" className="fs-sm">
                  <Trans t={t}>The input did not match any Automated Answer. It will be recorded as a <Typography.Text strong className="fs-sm">Missed Question</Typography.Text> in real use.</Trans>
                </Typography.Text>
              </Col>
            </Col>
          ) : null
          }

          {
            matchingAutomatedAnswer || processedMessage?.matchedCorpus?.id ? (
              <Col span={24} className="border-left-highlight pl-3 mb-4">
                <Form.Item
                  label={t('Matched Automated Answer')}
                  className="mb-1"
                >
                  {
                    matchingAutomatedAnswer ? (
                      <Fragment>
                        <Space direction="vertical" className="d-block">
                          <Typography.Text type="secondary" className="fs-sm d-block mb-2">
                            <Space>
                              {t('Name')}

                              <Tooltip title={t('The ID of the automated answer, which is automatically generated by the console.')}>
                                <QuestionCircleOutlined />
                              </Tooltip>
                            </Space>
                          </Typography.Text>

                          <Typography.Text italic className="bg-layout d-inline-block px-2 py-1 mr-3">
                            <ActivityLink
                              entryPointId={matchingAutomatedAnswer?.content.nodes.find(n => n.type === NODE_TYPES.INTRO)?.id}
                              setSelectedAA={setSelectedAA}
                            />
                          </Typography.Text>
                        </Space>

                        <Space direction="vertical" className="mt-3">
                          <Typography.Text type="secondary" className="fs-sm d-block mb-2">
                            <Space>
                              {t('Group')}
                              <Tooltip title={t('Overarching themes for grouping the automated answers that fall under a same group.')}>
                                <QuestionCircleOutlined />
                              </Tooltip>
                            </Space>
                          </Typography.Text>

                          <Typography.Text italic className="bg-layout d-inline-block px-2 py-1 mr-3">
                            {matchingAutomatedAnswer?.group[currentBotLanguage]}
                          </Typography.Text>
                        </Space>
                      </Fragment>
                    ) : (
                      <Space direction="vertical">
                        <Typography.Text type="secondary" className="fs-sm d-block mb-2">
                          <Space>
                            {t('Name')}
                            <Tooltip title={t('The ID of the automated answer, which is automatically generated by the console.')}>
                              <QuestionCircleOutlined />
                            </Tooltip>
                          </Space>
                        </Typography.Text>

                        <Typography.Text italic className="bg-layout d-inline-block px-2 py-1 mr-3">
                          {processedMessage?.matchedCorpus?.id}
                        </Typography.Text>
                      </Space>
                    )
                  }
                </Form.Item>
              </Col>
            ) : null
          }

          {
            (processedMessage?.examinedCorpus || []).length > 0 ? (
              <Col span={24} className="border-left-highlight pl-3 mb-4">
                <Form.Item
                  label={t('Examined Automated Answers')}
                  className="mb-1"
                >
                  <Carousel
                    ref={carouselRef}
                    className="p-0"
                  >
                    {renderCarouselItems()}
                  </Carousel>
                </Form.Item>
              </Col>
            ) : null
          }

          {
            processedMessage?.extractedParameters && !is.emptyArray(processedMessage?.extractedParameters) ? (
              <Col span={24} className="border-left-highlight pl-3 mb-4">
                <Form.Item
                  label={t('Extracted Parameters')}
                  className="mb-1"
                >
                  <Space direction="vertical">
                    {
                      processedMessage.extractedParameters.map((parameter, index) => (
                        <Typography.Text key={index} italic className="bg-layout d-inline-block px-2 py-1">
                          {parameter.key}: {parameter.value}
                        </Typography.Text>
                      ))
                    }
                  </Space>
                </Form.Item>
              </Col>
            ) : null
          }

          <Col span={24} className="border-left-highlight pl-3 mb-4">
            <Form.Item
              label={t('Agent responses')}
              tooltip={{
                icon: <QuestionCircleOutlined />,
                title: t('The responses that are given by the agent when the end-user has typed a phrase that triggers a given automated answer.')
              }}
              className="mb-1"
            >
              {
                matchingAutomatedAnswer ? renderBotMessages(matchingAutomatedAnswer) : renderBotMessages(defaultFallbackActivity)
              }
            </Form.Item>
          </Col>
        </Row>
      );
    }

    // Else
    // Empty state, there is either no response or the response is empty
    return (
      <Empty
        image={Empty.PRESENTED_IMAGE_SIMPLE}
        description={t('No Automated Answer detected')}
        className="text-muted"
      />
    );
  };

  const renderRecentlyTestedPhrases = () => {
    return recentlyTestedPhrases && !is.emptyArray(recentlyTestedPhrases) ? (
      <Fragment>
        <Row>
          <Divider className="my-2" />

          <Col span={24} className="px-5 pt-2">
            <Form.Item
              label={t('Recently tested')}
              tooltip={{
                icon: <QuestionCircleOutlined />,
                title: t('The user input that was most recently tested.')
              }}
            >
              {
                recentlyTestedPhrases.map(phrase => (
                  <Typography.Text
                    className="d-block cursor-pointer"
                    onClick={() => {
                      form.setFieldValue('trainingPhrase', phrase);
                      form.submit();
                    }}
                  >
                    {phrase}
                  </Typography.Text>
                ))
              }
            </Form.Item>
          </Col>
        </Row>
      </Fragment>
    ) : null;
  };

  const renderContent = () => {
    return (
      <Form
        form={form}
        name="TestAutomatedAnswersPanel"
        layout="vertical"
        className="label-secondary label-colon"
        onFinish={ async () => {
          if(currentPipelineId === null) {
            await processMessageDefault({
              orgId,
              tenantId,
              ...bot?.systemSettings?.nlpService === 'HBF_NLP'
                ? {
                  apiUrl: API_NLP_SERVICE_URL,
                  data: { query: form.getFieldValue('trainingPhrase'), language: currentBotLanguage, variables: {} }
                }
                : { urlParams: { query: encodeURIComponent(form.getFieldValue('trainingPhrase')), language: currentBotLanguage } }
            });
          } else {
            await processMessageWithNlp({
              orgId,
              tenantId,
              pipelineId: currentPipelineId,
              ...bot?.systemSettings?.nlpService === 'HBF_NLP'
                ? {
                  apiUrl: API_NLP_SERVICE_URL,
                  data: { query: form.getFieldValue('trainingPhrase'), language: currentBotLanguage, variables: {} }
                }
                : { urlParams: { query: encodeURIComponent(form.getFieldValue('trainingPhrase')), language: currentBotLanguage } }
            });
          }
          setRecentlyTestedPhrases([...new Set([form.getFieldValue('trainingPhrase'), ...recentlyTestedPhrases])].slice(0, 5));
          form.setFieldValue('trainingPhrase', '');
        }}
      >
        <Row>
          <Col span={24}>
            <div className="py-2 bg-layout border-bottom border-color-primary">
              <Form.Item
                name="trainingPhrase"
                noStyle
                className="w-100"
                rules={[{ required: true }]}
              >
                <AutoComplete
                  className="w-100"
                  options={recentlyTestedPhrases.filter(phrase => phrase !== form.getFieldValue('trainingPhrase')).map(phrase => ({ value: phrase }))}
                  filterOption={(inputValue, option) => {
                    return option?.value.toUpperCase().indexOf(inputValue.toUpperCase()) !== -1;
                  }}
                >
                  <Input.TextArea
                    autoSize={{ minRows: 1, maxRows: 3 }}
                    onPressEnter={(event) => {
                      if (!event.shiftKey) {
                        event.preventDefault();

                        if(!(bot && bot?.nlpMap && Object.keys(bot?.nlpMap).includes(currentBotLanguage)) && !currentPipelineId) {
                          return;
                        }

                        form.submit();
                      }
                    }}
                    className="w-100 px-5 bg-transparent"
                    bordered={false}
                    placeholder={t('Type user input')}
                  />
                </AutoComplete>
              </Form.Item>
            </div>
          </Col>
        </Row>

        {renderResults()}

        {renderRecentlyTestedPhrases()}

        <div className="float-right mt-6">
          <Space direction="vertical">
            <Space
              className="ant-space-cover ant-space-strech">
              {
                bot && bot?.nlpMap && Object.keys(bot?.nlpMap).includes(currentBotLanguage) ? (
                  <Link
                    to={replaceUrlParams(NAV_BOT, { orgId, tenantId }) + `?view=artificial-intelligence&tab=nlp-systems&pipelineId=${Object.entries(bot.nlpMap)?.find(([key, value]) => key === currentBotLanguage)[1]?.id}`}
                    target="_blank"
                    className="text-muted"
                    rel="noopener noreferrer"
                  >
                    {Object.entries(bot.nlpMap)?.find(([key, value]) => key === currentBotLanguage)[1]?.name}
                  </Link>
                ) : (
                  <Select
                    placeholder={t('Choose NLP System')}
                    value={currentPipelineId}
                    style={{ minWidth: '215px' }}
                    loading={isLoadingNlpPipelines}
                    disabled={isLoadingNlpPipelines}
                    onChange={pipelineId => {
                      setCurrentPipelineId(pipelineId);
                    }}
                    options={
                      nlpPipelines?.items?.map(nlpPipeline => {
                        return {
                          value: nlpPipeline.id,
                          name: nlpPipeline.name,
                          label: nlpPipeline.name
                        };
                      })}
                  />
                )
              }
            </Space>
            <Space>
              <Button
                type="primary"
                key="play"
                size="large"
                disabled={!(bot && bot?.nlpMap && Object.keys(bot?.nlpMap).includes(currentBotLanguage)) && !currentPipelineId}
                icon={<PlayCircleOutlined />}
                loading={isCreatingAutomatedAnswer || isUpdatingAutomatedAnswer}
                onClick={() => {
                  form.submit();
                }}
              >
                {t('Run Test')}
              </Button>

              <Button
                type="primary"
                key="reset"
                size="large"
                icon={<ReloadOutlined />}
                disabled={!(bot && bot?.nlpMap && Object.keys(bot?.nlpMap).includes(currentBotLanguage)) && !currentPipelineId}
                loading={isCreatingAutomatedAnswer || isUpdatingAutomatedAnswer}
                onClick={() => {
                  form.resetFields();
                  setRecentlyTestedPhrases([]);
                }}
              >
                {t('Reset')}
              </Button>
            </Space>
          </Space>

        </div>
      </Form>
    );
  };

  return (
    <Card
      title={
        <Row justify="space-between" align="middle">
          {t('Test Responses')}
          <Button
            icon={<CloseOutlined />}
            type="text"
            className="text-secondary"
            onClick={() => setIsTestPanelVisible(false)}
          />
        </Row>
      }
    >
      {renderContent()}
    </Card>
  );
};

TestAutomatedAnswersPanel.propTypes = {
  setIsTestPanelVisible: PropTypes.func,
  setSelectedAA: PropTypes.func
};

export default TestAutomatedAnswersPanel;
